import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Plus, ChevronDown, ChevronUp, Pencil, Trash2, TrendingUp, TrendingDown,
  Calendar as CalIcon, Coffee, Star, Tag as TagIcon, Receipt, Copy,
} from "lucide-react";
import CopyRatesDialog from "./CopyRatesDialog";
import { errMsg } from "@/lib/errors";

const CANCEL_LABEL = {
  flexible_7d: "Flessibile - 7 giorni",
  flexible_21d: "Flessibile - 21 giorni",
  non_refundable: "Non Rimborsabile",
  monthly: "Tariffa mensile (non rimborsabile)",
};

const TAG_TONE = {
  "Rigido": "bg-slate-100 text-slate-700 border-slate-300",
  "Prenotazioni anticipate": "bg-blue-50 text-blue-700 border-blue-300",
  "Non rimborsabile": "bg-rose-50 text-rose-700 border-rose-300",
  "Mensile": "bg-violet-50 text-violet-700 border-violet-300",
  "Last minute": "bg-pink-50 text-pink-700 border-pink-300",
};

const PERIODS = [
  { value: 30, label: "30 giorni" },
  { value: 90, label: "3 mesi" },
  { value: 180, label: "6 mesi" },
  { value: 365, label: "12 mesi" },
];

export default function RatePlansTab() {
  const [plans, setPlans] = useState([]);
  const [stats, setStats] = useState({});
  const [rooms, setRooms] = useState([]);
  const [period, setPeriod] = useState(30);
  const [expanded, setExpanded] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [copyDialogOpen, setCopyDialogOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      // Use Promise.allSettled so that a failure in one endpoint (e.g. stats)
      // doesn't blank out the other lists (rooms / plans).
      const [pr, sr, rr] = await Promise.allSettled([
        api.get("/admin/rate-plans"),
        api.get(`/admin/rate-plans/stats?period_days=${period}`),
        api.get("/rooms"),
      ]);
      if (pr.status === "fulfilled") {
        setPlans(pr.value.data.rate_plans || []);
      } else {
        toast.error("Errore caricamento piani tariffari");
      }
      if (sr.status === "fulfilled") {
        setStats(sr.value.data.stats || {});
      }
      if (rr.status === "fulfilled") {
        setRooms(rr.value.data || []);
      } else {
        toast.error("Errore caricamento camere — la lista nel modal sarà vuota.");
      }
    } catch {
      toast.error("Errore caricamento piani tariffari");
    }
  }, [period]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing({
      name: "",
      cancellation_type: "flexible_7d",
      discount_pct: 0,
      room_ids: [],
      meal_plan: "colazione",
      min_stay: 0,
      min_advance_days: 0,
      max_advance_days: 0,
      tags: [],
      active: true,
      sort_order: 100,
    });
    setDialogOpen(true);
  };

  const openEdit = (plan) => {
    setEditing({ ...plan });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!editing?.name?.trim()) {
      toast.error("Inserisci il nome del piano");
      return;
    }
    try {
      const body = { ...editing };
      delete body.id;
      delete body.created_at;
      delete body.updated_at;
      if (editing.id) {
        await api.put(`/admin/rate-plans/${editing.id}`, body);
        toast.success("Piano aggiornato");
      } else {
        await api.post("/admin/rate-plans", body);
        toast.success("Piano creato");
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(errMsg(e, "Errore salvataggio"));
    }
  };

  const del = async (plan) => {
    if (!window.confirm(`Eliminare il piano "${plan.name}"?`)) return;
    try {
      await api.delete(`/admin/rate-plans/${plan.id}`);
      toast.success("Piano eliminato");
      load();
    } catch {
      toast.error("Errore eliminazione");
    }
  };

  const roomsApplied = (plan) =>
    plan.room_ids?.length
      ? rooms.filter((r) => plan.room_ids.includes(r.id))
      : rooms;

  const priceDescription = (plan) => {
    if (plan.discount_pct === 0) return "In base al tuo Calendario";
    const pct = Math.abs(plan.discount_pct);
    return plan.discount_pct < 0
      ? `${pct}% più economico di Standard Rate`
      : `${pct}% più caro di Standard Rate`;
  };

  return (
    <div className="space-y-4" data-testid="rate-plans-tab">
      {/* HEADER — iter224: layout responsive.
          Mobile: titolo, periodo, bottoni impilati verticalmente a piena
          larghezza per non uscire fuori schermo. I bottoni mostrano testo
          abbreviato ("Copia" / "Aggiungi") su mobile e testo completo su
          desktop tramite hidden sm:inline. */}
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl text-[#1E3A5F] flex items-center gap-2">
            <Receipt className="w-6 h-6 text-[#AC5D45]" />Piani tariffari
          </h2>
          <p className="text-sm text-[#635F59] mt-1 max-w-2xl">
            Crea e aggiorna vari tipi di piani tariffari per i tuoi clienti. Puoi
            gestire le tariffe e la disponibilità dal tuo <a href="/admin/calendar" className="text-[#AC5D45] hover:underline font-medium">Calendario</a>.
          </p>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
          <div className="flex bg-[#FDFBF7] border border-[#E8E2D9] rounded-full p-1 text-xs self-start">
            {PERIODS.map((p) => (
              <button
                key={p.value}
                onClick={() => setPeriod(p.value)}
                data-testid={`period-${p.value}`}
                className={`px-3 py-1 rounded-full transition ${
                  period === p.value ? "bg-white shadow-sm text-[#1E3A5F] font-semibold" : "text-[#635F59]"
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <Button
              onClick={() => setCopyDialogOpen(true)}
              variant="outline"
              data-testid="rate-plan-copy-future"
              className="border-[#AC5D45] text-[#AC5D45] hover:bg-[#FDF1E6] w-full sm:w-auto justify-center"
            >
              <Copy className="w-4 h-4 mr-1" />
              <span className="sm:hidden">Copia tariffe</span>
              <span className="hidden sm:inline">Copia tariffe nelle date future</span>
            </Button>
            <Button
              onClick={openCreate}
              data-testid="rate-plan-create"
              className="bg-[#1E3A5F] hover:bg-[#152A45] text-white w-full sm:w-auto justify-center"
            >
              <Plus className="w-4 h-4 mr-1" />
              <span className="sm:hidden">Aggiungi piano</span>
              <span className="hidden sm:inline">Aggiungi un nuovo piano tariffario</span>
            </Button>
          </div>
        </div>
      </div>

      {/* HEADER ROW */}
      <div className="hidden md:grid grid-cols-12 gap-2 text-xs uppercase tracking-wider text-[#635F59] font-semibold px-4 py-2 border-b border-[#E8E2D9]">
        <div className="col-span-3">Nome del piano</div>
        <div className="col-span-3">Condizioni di cancellazione</div>
        <div className="col-span-2">Prezzo</div>
        <div className="col-span-2 text-right">% Cancellazioni</div>
        <div className="col-span-2 text-right">Ricavo netto</div>
      </div>

      {/* PLANS LIST */}
      <div className="space-y-2">
        {plans.map((plan) => {
          const isOpen = expanded[plan.id];
          const st = stats[plan.id] || {};
          const applied = roomsApplied(plan);
          return (
            <div
              key={plan.id}
              className="bg-white border border-[#E8E2D9] rounded-xl overflow-hidden"
              data-testid={`rate-plan-${plan.id}`}
            >
              {/* COMPACT ROW.
                  iter224: mobile ora mostra piccole etichette sopra ogni
                  valore (Cancellazione, Prezzo, % Cancellazioni, Ricavo)
                  così l'host capisce cosa sta guardando. Su desktop
                  restano nascoste perché già presenti nell'header sopra. */}
              <button
                type="button"
                onClick={() => setExpanded({ ...expanded, [plan.id]: !isOpen })}
                className="w-full grid grid-cols-12 gap-2 items-start md:items-center p-4 text-left hover:bg-[#FDFBF7] transition"
                data-testid={`rate-plan-toggle-${plan.id}`}
              >
                <div className="col-span-12 md:col-span-3 flex items-center gap-2">
                  {isOpen ? <ChevronUp className="w-4 h-4 text-[#AC5D45]" /> : <ChevronDown className="w-4 h-4 text-[#635F59]" />}
                  <div>
                    <div className="font-semibold text-[#1E3A5F]">{plan.name}</div>
                    <div className="text-[10px] text-[#635F59] font-mono">ID {plan.id.slice(0, 8)}</div>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {(plan.tags || []).map((tag) => (
                        <Badge
                          key={tag}
                          variant="outline"
                          className={`text-[10px] ${TAG_TONE[tag] || "bg-slate-50 border-slate-200"}`}
                        >
                          {tag}
                        </Badge>
                      ))}
                      {!plan.active && <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-300">Inattivo</Badge>}
                    </div>
                  </div>
                </div>
                <div className="col-span-12 md:col-span-3 text-sm text-[#1E3A5F]">
                  <div className="text-[10px] uppercase tracking-wider text-[#635F59] font-semibold mb-0.5 md:hidden">Cancellazione</div>
                  {CANCEL_LABEL[plan.cancellation_type] || plan.cancellation_type}
                </div>
                <div className="col-span-12 md:col-span-2 text-sm text-[#635F59]">
                  <div className="text-[10px] uppercase tracking-wider text-[#635F59] font-semibold mb-0.5 md:hidden">Prezzo</div>
                  {priceDescription(plan)}
                </div>
                <div className="col-span-6 md:col-span-2 text-left md:text-right text-sm">
                  <div className="text-[10px] uppercase tracking-wider text-[#635F59] font-semibold mb-0.5 md:hidden">% Cancellazioni</div>
                  {st.count ? (
                    <span className={`inline-flex items-center gap-1 ${st.cancellation_pct > 30 ? "text-rose-700" : "text-[#1E3A5F]"}`}>
                      {st.cancellation_pct > 30 ? <TrendingDown className="w-3 h-3" /> : <TrendingUp className="w-3 h-3" />}
                      {st.cancellation_pct}%
                    </span>
                  ) : (
                    <span className="text-[10px] text-[#A8A6A2]">Dati insufficienti</span>
                  )}
                </div>
                <div className="col-span-6 md:col-span-2 text-right font-display text-base text-[#1E3A5F]">
                  <div className="text-[10px] uppercase tracking-wider text-[#635F59] font-semibold mb-0.5 md:hidden font-sans">Ricavo netto</div>
                  {st.net_revenue ? `€ ${st.net_revenue.toFixed(2)}` : <span className="text-[10px] text-[#A8A6A2] font-sans">Dati insufficienti</span>}
                </div>
              </button>

              {/* EXPANDED */}
              {isOpen && (
                <div className="border-t border-[#E8E2D9] bg-[#FDFBF7] p-4 space-y-3" data-testid={`rate-plan-detail-${plan.id}`}>
                  <div className="grid md:grid-cols-5 gap-3 text-xs">
                    <DetailColumn title="Tipologie di alloggio">
                      <ul className="space-y-1">
                        {applied.map((r) => (
                          <li key={r.id} className="flex items-center gap-1.5 text-[#1E3A5F]">
                            <span className="w-1 h-1 rounded-full bg-[#AC5D45]" />
                            {r.name?.it || r.slug}
                          </li>
                        ))}
                      </ul>
                    </DetailColumn>
                    <DetailColumn title="Pasti">
                      {plan.meal_plan === "colazione" ? (
                        <span className="inline-flex items-center gap-1 text-[#1E3A5F]"><Coffee className="w-3.5 h-3.5" />Colazione</span>
                      ) : (
                        <span className="text-[#635F59]">Senza pasti</span>
                      )}
                    </DetailColumn>
                    <DetailColumn title="Vantaggi extra">
                      {plan.discount_pct < 0 ? (
                        <span className="text-emerald-700 font-semibold">{Math.abs(plan.discount_pct)}% di sconto</span>
                      ) : (
                        <span className="text-[#635F59]">Nessun vantaggio extra</span>
                      )}
                    </DetailColumn>
                    <DetailColumn title="Soggiorno minimo">
                      {plan.min_stay > 0 ? (
                        <span className="inline-flex items-center gap-1 text-[#1E3A5F]"><Star className="w-3 h-3" />Min {plan.min_stay} notti</span>
                      ) : (
                        <span className="text-[#635F59]">Nessuna durata minima</span>
                      )}
                    </DetailColumn>
                    <DetailColumn title="Periodo prenotabile">
                      {plan.min_advance_days > 0 ? (
                        <span className="text-[#1E3A5F]">Min {plan.min_advance_days}g anticipo</span>
                      ) : plan.max_advance_days > 0 ? (
                        <span className="text-[#1E3A5F]">Max {plan.max_advance_days}g anticipo</span>
                      ) : (
                        <span className="text-[#635F59]">Sempre</span>
                      )}
                    </DetailColumn>
                  </div>
                  <div className="flex gap-2 justify-end pt-2 border-t border-[#E8E2D9]">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openEdit(plan)}
                      data-testid={`rate-plan-edit-${plan.id}`}
                      className="border-blue-300 text-blue-700 hover:bg-blue-50"
                    >
                      <Pencil className="w-3.5 h-3.5 mr-1" />Modifica
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => del(plan)}
                      data-testid={`rate-plan-delete-${plan.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5 mr-1" />Elimina
                    </Button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
        {plans.length === 0 && (
          <div className="bg-[#FDFBF7] border border-dashed border-[#E8E2D9] rounded-xl p-10 text-center text-[#635F59]">
            <Receipt className="w-12 h-12 mx-auto mb-3 text-[#A8A6A2]" />
            <p className="font-semibold text-[#1E3A5F]">Nessun piano tariffario configurato</p>
            <p className="text-sm mt-1">Crea il tuo primo piano per offrire ai clienti opzioni di prezzo diverse.</p>
            <Button
              onClick={openCreate}
              className="mt-4 bg-[#1E3A5F] hover:bg-[#152A45] text-white"
              data-testid="rate-plan-create-empty"
            >
              <Plus className="w-4 h-4 mr-1" />Crea piano
            </Button>
          </div>
        )}
      </div>

      {/* EDIT DIALOG */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="rate-plan-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-[#1E3A5F] flex items-center gap-2">
              <Receipt className="w-5 h-5" />
              {editing?.id ? "Modifica piano tariffario" : "Nuovo piano tariffario"}
            </DialogTitle>
            <DialogDescription>
              Definisci nome, condizioni di cancellazione, sconto e camere a cui si applica.
            </DialogDescription>
          </DialogHeader>

          {editing && (
            <div className="space-y-4">
              {/* Name */}
              <div>
                <Label>Nome del piano *</Label>
                <Input
                  value={editing.name}
                  onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                  placeholder="Es. Standard Rate, Last Minute -20%, Tariffa mensile"
                  data-testid="rate-plan-name"
                  className="mt-1.5 h-10"
                />
              </div>

              {/* Cancellation */}
              <div>
                <Label>Condizioni di cancellazione</Label>
                <Select
                  value={editing.cancellation_type}
                  onValueChange={(v) => setEditing({ ...editing, cancellation_type: v })}
                >
                  <SelectTrigger className="mt-1.5 h-10" data-testid="rate-plan-cancellation">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(CANCEL_LABEL).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Discount */}
              <div>
                <Label>Sconto / Sovrapprezzo % rispetto al prezzo base</Label>
                <div className="flex items-center gap-2 mt-1.5">
                  <Input
                    type="number"
                    step="1"
                    min="-50"
                    max="50"
                    value={editing.discount_pct}
                    onChange={(e) => setEditing({ ...editing, discount_pct: parseFloat(e.target.value) || 0 })}
                    placeholder="0"
                    data-testid="rate-plan-discount"
                    className="h-10 max-w-[120px]"
                  />
                  <span className="text-sm text-[#635F59]">% (negativo = sconto, positivo = sovrapprezzo, 0 = standard)</span>
                </div>
              </div>

              {/* Meal plan & Min stay */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Pasti</Label>
                  <Select value={editing.meal_plan} onValueChange={(v) => setEditing({ ...editing, meal_plan: v })}>
                    <SelectTrigger className="mt-1.5 h-10" data-testid="rate-plan-meal">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="colazione">Colazione inclusa</SelectItem>
                      <SelectItem value="none">Senza pasti</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Soggiorno min. (notti)</Label>
                  <Input
                    type="number"
                    min="0"
                    value={editing.min_stay}
                    onChange={(e) => setEditing({ ...editing, min_stay: parseInt(e.target.value) || 0 })}
                    data-testid="rate-plan-min-stay"
                    className="mt-1.5 h-10"
                  />
                </div>
              </div>

              {/* Advance days */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Anticipo min. prenotazione (giorni)</Label>
                  <Input
                    type="number"
                    min="0"
                    value={editing.min_advance_days}
                    onChange={(e) => setEditing({ ...editing, min_advance_days: parseInt(e.target.value) || 0 })}
                    data-testid="rate-plan-min-adv"
                    className="mt-1.5 h-10"
                  />
                </div>
                <div>
                  <Label>Anticipo max. prenotazione (giorni)</Label>
                  <Input
                    type="number"
                    min="0"
                    value={editing.max_advance_days}
                    onChange={(e) => setEditing({ ...editing, max_advance_days: parseInt(e.target.value) || 0 })}
                    data-testid="rate-plan-max-adv"
                    className="mt-1.5 h-10"
                    placeholder="0 = no max"
                  />
                </div>
              </div>

              {/* Rooms (multiselect) */}
              <div>
                <Label>Camere a cui si applica</Label>
                <p className="text-[11px] text-[#635F59] mt-0.5">Lascia vuoto per applicarlo a tutte le camere.</p>
                <div className="mt-2 grid grid-cols-2 gap-1.5 max-h-32 overflow-y-auto border border-[#E8E2D9] rounded p-2 bg-white">
                  {rooms.length === 0 && (
                    <div className="col-span-2 text-center text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-1.5">
                      ⚠️ Lista camere non caricata. Chiudi il modal, premi <b>Aggiorna</b> in alto e riapri.
                    </div>
                  )}
                  {rooms.map((r) => {
                    const checked = editing.room_ids.includes(r.id);
                    return (
                      <label key={r.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-[#FDFBF7] px-1 py-0.5 rounded">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => {
                            const next = e.target.checked
                              ? [...editing.room_ids, r.id]
                              : editing.room_ids.filter((x) => x !== r.id);
                            setEditing({ ...editing, room_ids: next });
                          }}
                          data-testid={`rate-plan-room-${r.slug}`}
                        />
                        {r.name?.it || r.slug}
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Tags */}
              <div>
                <Label>Tag (separati da virgola)</Label>
                <Input
                  value={(editing.tags || []).join(", ")}
                  onChange={(e) => setEditing({ ...editing, tags: e.target.value.split(",").map((t) => t.trim()).filter(Boolean) })}
                  placeholder="Es. Rigido, Non rimborsabile, Mensile"
                  data-testid="rate-plan-tags"
                  className="mt-1.5 h-10"
                />
              </div>

              {/* Active */}
              <div className="flex items-center justify-between bg-[#FDFBF7] border border-[#E8E2D9] rounded-lg p-3">
                <div>
                  <Label className="cursor-pointer">Piano attivo</Label>
                  <p className="text-[11px] text-[#635F59]">I piani inattivi non vengono mostrati al cliente in fase di prenotazione.</p>
                </div>
                <Switch
                  checked={editing.active}
                  onCheckedChange={(v) => setEditing({ ...editing, active: v })}
                  data-testid="rate-plan-active"
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Annulla</Button>
            <Button onClick={save} data-testid="rate-plan-save" className="bg-[#AC5D45] hover:bg-[#8A4936] text-white">
              {editing?.id ? "Salva modifiche" : "Crea piano"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CopyRatesDialog
        open={copyDialogOpen}
        onOpenChange={setCopyDialogOpen}
        rooms={rooms}
        ratePlans={plans}
      />
    </div>
  );
}

const DetailColumn = ({ title, children }) => (
  <div>
    <div className="text-[10px] uppercase tracking-wider text-[#635F59] font-semibold mb-1.5">{title}</div>
    <div>{children}</div>
  </div>
);
